import React, { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserPlus, Send, CheckCircle2, Clock, Loader2, Mail, LogIn } from "lucide-react";

const fmt = (iso) => { if (!iso) return "never"; try { return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

export const PortalAccessCard = ({ leadId }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/leads/${leadId}/member`); setStatus(data); }
    catch (e) { /* silent — lead may be out of scope */ }
    finally { setLoading(false); }
  }, [leadId]);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/leads/${leadId}/invite-member`, { base_url: window.location.origin }); toast.success(data.message || `Invitation sent to ${data.masked_email}.`); load(); }
    catch (e) { toast.error(errMsg(e, "Could not send the invite.")); }
    finally { setBusy(false); }
  };
  const resend = async () => {
    if (!status?.user_id) return;
    setBusy(true);
    try { const { data } = await api.post(`/members/${status.user_id}/resend-invite`, { base_url: window.location.origin }); toast.success(`Invite re-sent to ${data.masked_email}.`); load(); }
    catch (e) { toast.error(errMsg(e, "Could not resend.")); }
    finally { setBusy(false); }
  };

  if (loading) return null;
  if (!status) return null;

  return (
    <Card className="mt-4 p-4 border-[#E6EDF2]" data-testid="lead-portal-access-card">
      <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2 flex items-center gap-1.5"><LogIn size={13} /> Family portal access</div>
      {!status.account_exists ? (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[13px] text-[#4B5563]">
            No free member account yet.{" "}
            {status.can_invite ? <>Invite <b className="text-[#252221]">{status.email}</b> to their private portal.</> : <span className="text-[#9C1E22]">Add an email to this lead first.</span>}
          </div>
          <Button size="sm" onClick={invite} disabled={busy || !status.can_invite} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1.5" data-testid="lead-invite-member-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Invite to portal
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-[13px]">
            {status.invite_pending ? <Clock size={15} className="text-[#B7791F]" /> : <CheckCircle2 size={15} className="text-[#2F7D5A]" />}
            <span className="font-medium text-[#252221]" data-testid="lead-member-status">{status.invite_pending ? "Invite pending — hasn't set a password yet" : "Active free member"}</span>
          </div>
          <div className="text-[12px] text-[#4B5563] flex flex-wrap gap-x-4 gap-y-0.5">
            <span className="flex items-center gap-1"><Mail size={12} /> {status.email}</span>
            <span>Last login: {fmt(status.last_login)}</span>
          </div>
          <Button size="sm" variant="outline" onClick={resend} disabled={busy} className="border-[#D6E0E7] text-[#1C4358] gap-1.5" data-testid="lead-resend-invite-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {status.invite_pending ? "Resend invite" : "Send a fresh sign-in link"}
          </Button>
        </div>
      )}
    </Card>
  );
};
